import { OUTLOOK_INFO, type OutlookDay } from "../../../shared/weatherDetail";
import { useHousehold } from "../../app/useHousehold";
import { dataStateOf } from "../../data/queries";
import { useGrid, useOutlook } from "../../data/weatherQueries";
import { EmptyState } from "../../ui/StatusBadge";
import { useNow } from "../../ui/time";
import { NeedsLocation, remPx, SkyFoot } from "../sky/common";
import type { WidgetDefinition, WidgetProps } from "../types";
import { WidgetHeader } from "../WidgetHeader";
import "./nerd.css";

type OutlookConfig = Record<string, never>;

/** Storm Prediction Center severe-storm risk at home: today, tomorrow and day 3, on SPC's 0–5 scale. */
function StormOutlook({ size, fullscreen, openFullscreen }: WidgetProps<OutlookConfig>) {
  const { settings, timeZone } = useHousehold();
  useNow("minute");
  const q = useOutlook();
  const grid = useGrid();
  const state = dataStateOf(q, 90 * 60_000);
  if (!settings.location) return <NeedsLocation icon="bolt" title="The storm outlook needs a home location" detail="Set a general location to see severe-storm risk." compact={size.variant === "compact"} />;
  const data = q.data;
  if (!data) return state === "loading" ? <div className="widget-loading" /> : <EmptyState icon="bolt" title="Storm outlook unavailable" detail={q.error?.message} />;

  const thunder = Math.max(0, ...(grid.data?.hours.slice(0, 24).map((h) => h.thunderPct ?? 0) ?? [0]));
  const worst = data.days.reduce<OutlookDay | null>((w, d) => (d.label && (!w?.label || OUTLOOK_INFO[d.label].level > OUTLOOK_INFO[w.label].level) ? d : w), null);
  const dayName = (d: OutlookDay) =>
    d.day === 1 ? "Today" : d.day === 2 ? "Tomorrow" : d.validFrom ? new Intl.DateTimeFormat("en-US", { timeZone, weekday: "long" }).format(new Date(Date.parse(d.validFrom) + 12 * 3_600_000)) : "Day 3";

  // Short cards lay the three days side by side and drop the explanation.
  const strip = size.height < remPx() * 13.5;
  if (strip) {
    return (
      <div className="nerd nerd-outlook is-strip">
        <WidgetHeader title="Severe storm outlook" meta={thunder >= 10 ? `Thunder ${thunder}% today` : "SPC"} onTitle={fullscreen ? undefined : openFullscreen} />
        <ul className="outlook-strip">
          {data.days.map((d) => {
            const info = d.label ? OUTLOOK_INFO[d.label] : null;
            return (
              <li key={d.day} className={info ? "" : "none"} style={info ? { ["--risk" as string]: info.color } : undefined}>
                <span className="outlook-name">{d.day === 1 ? "Today" : d.day === 2 ? "Tomorrow" : dayName(d).slice(0, 3)}</span>
                <strong>{info ? (info.level === 0 ? "Storms" : `${info.name} ${info.level}/5`) : "None"}</strong>
              </li>
            );
          })}
        </ul>
      </div>
    );
  }

  return (
    <div className="nerd nerd-outlook">
      <WidgetHeader title="Severe storm outlook" meta={thunder >= 10 ? `Thunder chance today ${thunder}%` : "Storm Prediction Center"} onTitle={fullscreen ? undefined : openFullscreen} />
      <ul className="outlook-days">
        {data.days.map((d) => {
          const info = d.label ? OUTLOOK_INFO[d.label] : null;
          return (
            <li key={d.day} className="outlook-day">
              <span className="outlook-name">{dayName(d)}</span>
              <span className={`outlook-pill ${info ? "" : "none"}`} style={info ? { ["--risk" as string]: info.color } : undefined}>
                {info ? (info.level === 0 ? "Thunderstorms" : `${info.name} · ${info.level} of 5`) : "No storms expected"}
              </span>
              <span className="outlook-scale" aria-hidden="true">
                {[1, 2, 3, 4, 5].map((l) => (
                  <i key={l} className={info && info.level >= l ? "on" : ""} style={info && info.level >= l ? { ["--risk" as string]: info.color } : undefined} />
                ))}
              </span>
            </li>
          );
        })}
      </ul>
      {worst?.label && size.variant !== "compact" && <p className="outlook-meaning">{OUTLOOK_INFO[worst.label].meaning}</p>}
      {size.variant !== "compact" && <SkyFoot source="NOAA Storm Prediction Center" stamp={data.stamp} state={state} updatedAt={q.dataUpdatedAt} />}
    </div>
  );
}

export const outlookWidget: WidgetDefinition<OutlookConfig> = {
  type: "storm-outlook",
  version: 1,
  name: "Severe storm outlook",
  description: "The Storm Prediction Center’s severe-thunderstorm risk for home, today through day 3, on its 1–5 scale.",
  icon: "bolt",
  category: "weather",
  defaultSize: { w: 4, h: 3 },
  minSize: { w: 3, h: 2 },
  stackHeight: 260,
  defaults: {},
  settings: [],
  provider: "spc",
  capabilities: { requiresLocation: true },
  supportsFullscreen: true,
  Component: StormOutlook,
};
